import {inject, Injectable, isDevMode} from '@angular/core';
import {codeToToken, login, refreshToken} from '@ekultur/authentication';
import {BehaviorSubject, Subject} from 'rxjs';
import {environment} from '../../environments/environment';
import {AccessTokenService} from './access-token.service';
import {LoggerService} from './logger.service';
import {HibernationFlowService} from '../shared/hibernation-flow.service';
import {FetchTimeoutError, fetchWithTimeout} from '../shared/fetch-with-timeout';

export interface ApplicationResponseApplication {
  id: string;
}

export interface ApplicationResponseMuseum {
  applications: ApplicationResponseApplication[];
  id: string;
}

export interface ApplicationResponse {
  museums: ApplicationResponseMuseum[]
}

export interface MuseumResponseMuseum {
  id: string;
  name: string;
  short_name: string;
  profile: string;
  logo: string;
}

export interface MuseumResponse {
  museums: MuseumResponseMuseum[]
}

export interface PrimusServerUrl {
  url: string;
  museumId: string;
}

export interface PrimusInstanceDetails {
  id: string;
  name: string;
  shortName: string;
  api: string;
  online: boolean;
  profile: string;
  logo: string;
  checked: boolean;
}

interface ApiAuthRequest {
  museum: PrimusInstanceDetails;
  cb?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class PrimusBackendInstanceService {
  // Injections
  private readonly accessTokenService: AccessTokenService = inject(AccessTokenService);
  private readonly hibernationFlow: HibernationFlowService = inject(HibernationFlowService);
  private readonly logger: LoggerService = inject(LoggerService);

  constructor() {
    this.loginURL = environment.ekulturLoginGatewayURL;
    this.apiURL = environment.ekulturAPIGatewayURL;
    this.appID = environment.ekulturPrimusAppID;
  }

  responseStatusSubject = new Subject();

  private static readonly logID = '[EKAuthService]';

  private static readonly STORAGE_KEY = 'primus.client.main';
  private readonly loginURL: string;
  private readonly apiURL: string;
  private readonly appID: string;
  private readonly apiLoginTimeoutMs = 15000;
  private apiAuthInFlight = false;
  private pendingApiAuth: ApiAuthRequest | null = null;
  private apiAuthenticated = false;
  private userAuthenticated = false;

  authenticated: BehaviorSubject<any> = new BehaviorSubject(false);
  authError: BehaviorSubject<string> = new BehaviorSubject('');

  static getApiUrl(): string {
    return this.getInstanceDetails()?.api;
  }

  static cleanInstanceDetails() {
    window.localStorage.removeItem(this.STORAGE_KEY);
  }

  static getInstanceDetails(): PrimusInstanceDetails {
    return JSON.parse(window.localStorage.getItem(this.STORAGE_KEY));
  }

  static saveInstanceDetails(museum: PrimusInstanceDetails) {
    window.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(museum));
  }

  private static getCode(url: string): string | null {
    try {
      const parsed = new URL(url);
      const value = parsed.searchParams.get('code');
      if (value) {
        return value;
      }
    } catch (e) {
      // Fallback to regex parsing if URL parsing fails.
    }
    const re = new RegExp(/([0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12})/i);
    const matches = RegExp(re).exec(url);
    return matches?.[0] ?? null;
  }

  private static getState(url: string): string | null {
    try {
      const parsed = new URL(url);
      const value = parsed.searchParams.get('state');
      if (value) {
        return value;
      }
    } catch (e) {
      // Fallback to regex parsing if URL parsing fails.
    }
    const re = /[?&]state=([0-9A-Za-z_-]+)/;
    const matches = RegExp(re).exec(url);
    return matches?.[1] ?? null;
  }

  setApiAuthenticated(state: boolean) {
    this.apiAuthenticated = state;
  }

  isApiAuthenticated(): boolean {
    return this.apiAuthenticated;
  }

  async setRefreshTokenSetAuthenticated() {
    try {
      const hasAccessToken = !!localStorage.getItem('kit.access_token');
      const hasIdToken = !!localStorage.getItem('kit.id_token');
      const hasReportToken = !!localStorage.getItem('kit.generatingReport');

      if (hasReportToken && hasAccessToken && hasIdToken) {
        this.userAuthenticated = true;
        this.broadcastAuthStatus();
      } else {
        this.userAuthenticated = await this.setRefreshToken();
        this.broadcastAuthStatus();
      }
    } catch (e) {
      this.logger.error(`${PrimusBackendInstanceService.logID} -- ${e}`);
      this.userAuthenticated = false;
      this.broadcastAuthStatus();
      // If token code => token exchange failed, redirect user to login.
      this.eCultureLogin();
    }
  }

  async setRefreshToken(): Promise<boolean> {
    console.log('----- setRefreshToken', window.location.href);
    const url = window.location.href;
    const code = PrimusBackendInstanceService.getCode(url);
    const state = PrimusBackendInstanceService.getState(url);
    if (code && state) {
      return this.setJWTToken(url);
    }
    return this.eCultureRefreshToken();
  }

  async fetchApiLogin(authUrl: string, token: string): Promise<Response> {
    // Keep API-login on fetch instead of HttpClient/RxJS:
    // - we need the raw Response status/body behavior for existing auth handling
    // - AbortController timeout is lightweight and explicit for this single request path.
    return fetchWithTimeout(authUrl, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'Id-Token': localStorage.getItem('kit.id_token') ?? '',
        'Correlation-Id': this.generateCorrelationId(),
      },
      referrerPolicy: 'no-referrer-when-downgrade',
      method: 'GET',
    }, this.apiLoginTimeoutMs)
      .then(response => {

        // Feed new value to Subject.
        this.responseStatusSubject.next(response);

        if (!response.ok) {
          if (response?.status === 403) {
            throw Error(`${PrimusBackendInstanceService.logID} -- API-authentication failed, missing privileges`);
          } else if (response.status === 401) {
            window.alert('Not authenticated! You will be redirected to login')
            this.apiAuthFailedRedirectToLogin();
          } else if (response?.status !== 200) {
            throw Error(`${PrimusBackendInstanceService.logID} -- API-authentication failed`);
          }
        } else {
          this.setApiAuthenticated(true);
          this.broadcastAuthStatus();
          if (isDevMode()) {
            this.logger.debug(`${PrimusBackendInstanceService.logID} -- API authenticated`);
          }
        }

        return response;
      }).catch(e => {
      if (e instanceof FetchTimeoutError) {
        this.logger.error(`fetchApiLogin - timed out after ${this.apiLoginTimeoutMs} ms`);
      } else {
        this.logger.error('fetchApiLogin - failed: ', e);
      }
      throw e;
    });
  }

  apiAuthFailedRedirectToLogin() {
    PrimusBackendInstanceService.cleanInstanceDetails();
    this.setApiAuthenticated(false);
    setTimeout(() => {
      login({
        locationPostLogin: null,
        redirectUri: null,
        loginHost: environment.ekulturLoginGatewayURL
      });
    }, 500);
  }

  async apiAuthenticate(museum: PrimusInstanceDetails, cb?: () => void) {
    if (!museum) {
      // No backend selected yet. Keep auth stream in sync and stop.
      this.broadcastAuthStatus();
      return;
    }
    // Persist explicit museum choice immediately, even if another auth call is in-flight.
    // This keeps splash/wake messaging and later retries aligned with the latest user choice.
    PrimusBackendInstanceService.saveInstanceDetails(museum);
    if (this.apiAuthInFlight) {
      // If an auth run is already active, keep only the latest explicit selection.
      this.pendingApiAuth = {museum, cb};
      return;
    }
    this.apiAuthInFlight = true;

    let request: ApiAuthRequest | null = {museum, cb};
    try {
      while (request) {
        const currentRequest = request;
        // Consume pending slot; any newer selection arriving during this run
        // will repopulate pendingApiAuth and be picked up in the next loop.
        this.pendingApiAuth = null;

        // Selecting a backend starts wake/auth. Exit selector mode before wake check.
        this.hibernationFlow.setBackendSelectorVisible(false);
        await this.hibernationFlow.ensureAwakeOrThrow('api-authenticate');
        const authUrl = `${currentRequest.museum.api}/cms_api/v1.0/login`;
        const token = this.accessTokenService.getToken();
        const response = await this.fetchApiLogin(authUrl, token);

        // Ignore stale callback if a newer explicit selection arrived while this run was in-flight.
        if (response?.ok && currentRequest.cb && !this.pendingApiAuth) {
          currentRequest.cb();
        }

        request = this.pendingApiAuth;
      }
    } catch (e) {
      this.setApiAuthenticated(false);
      if (isDevMode()) {
        this.authError.next('API-authentication failed, retrying...');
      }
      this.broadcastAuthStatus();
      this.logger.error('Error fetching api login', e);
    } finally {
      this.pendingApiAuth = null;
      this.apiAuthInFlight = false;
    }
  }

  async getUserPrimusMuseumIds() {
    try {
      const headers = this.getAPIAuthHeader();
      const user = await this.getECultureUser();
      const userId = user['uniqueId'];
      const url = `${this.apiURL}/authz/users/${userId}/applications/`;
      const response = await fetch(url, {
        headers: headers
      });
      const data: ApplicationResponse = await response.json();
      return data?.museums?.filter(m => m.applications.find(a => a.id === this.appID))
        .map(m => m.id);
    } catch (e) {
      this.logger.error(`${PrimusBackendInstanceService.logID} -- ${e}`);
      this.authError.next('Failed to fetch organizations');
    }
    return null;
  }

  async getUserPrimusServers(): Promise<PrimusInstanceDetails[]> {
    const museumIds = await this.getUserPrimusMuseumIds();
    const museums = await this.getECultureMuseums();
    const serverUrls = await this.getPrimusServerUrls();

    const availableUrls =
      serverUrls.filter(u => museumIds.includes(u.museumId) && u.url !== '')
        .map(u => {
          return {'url': u.url, 'museumId': u.museumId};
        });

    const availableMuseums = museums.filter(m => museumIds.includes(m.id)).map(m => {
      return {
        'id': m.id,
        'name': m.name,
        'shortName': m.shortName,
        'profile': m.profile,
        'logo': m.logo
      } as PrimusInstanceDetails;
    });

    const userMuseums: PrimusInstanceDetails[] = availableMuseums
      .filter(m => museumIds.includes(m.id))
      .map(m => {
        return {
          'id': m.id,
          'name': m.name,
          'shortName': m.shortName,
          'profile': m.profile,
          'logo': m.logo,
          'api': availableUrls.find(url => url.museumId === m.id)?.url,
          checked: true,
          online: true,
        } as PrimusInstanceDetails;
      })
      .filter(um => um.api);

    if (isDevMode() || window.location.port === '8080') {
      userMuseums.push({
        name: 'Localhost',
        shortName: 'DEV',
        api: 'http://localhost:5000',
        logo: '',
        id: '20'
      } as PrimusInstanceDetails);
    }

    return userMuseums;
  }

  private async getECultureMuseums(): Promise<PrimusInstanceDetails[]> {
    const url = `${this.apiURL}/museum-api/museums`;
    const response = await fetch(url, {});
    const data: MuseumResponse = await response.json();
    return data.museums.map(m => {
      return {
        'id': m.id,
        'name': m.name,
        'shortName': m.short_name,
        'profile': (m.profile ? `https://${m.profile}` : ''),
        'logo': (m.logo ? `https://${m.logo}` : ''),
      } as PrimusInstanceDetails;
    });
  }

  private async getPrimusServerUrls(): Promise<PrimusServerUrl[]> {
    const headers = this.getAPIAuthHeader();
    const url = `${this.apiURL}/app-registry/urls/?appId=${this.appID}`;
    const response = await fetch(url, {
      headers: headers,
    });
    return response.json();
  }

  private async getECultureUser() {
    const url = `${this.apiURL}/authz/authorize/userData`;
    const headers = this.getAPIAuthHeader();
    if (headers) {
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });
      if (!response?.ok) {
        throw new Error(`${PrimusBackendInstanceService.logID} -- failed to load user`);
      }
      return response.json();
    } else {
      throw new Error(`${PrimusBackendInstanceService.logID} -- failed to load user invalid credentials`);
    }
  }

  private getAPIAuthHeader() {
    const token = this.accessTokenService.getToken();
    if (token) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Id-Token': localStorage.getItem('kit.id_token') ?? '',
        'Correlation-Id': this.generateCorrelationId()
      };
    } else {
      return null;
    }
  }

  private setJWTToken(url: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const code = PrimusBackendInstanceService.getCode(url);
      const state = PrimusBackendInstanceService.getState(url);
      if (code && state) {
        try {
          const sameSite = this.apiURL.indexOf('primus.org') !== -1 && !url.startsWith('http://localhost');
          // Exchange received code with token.
          codeToToken({
            code: code,
            state: state,
            sameSite: sameSite,
            redirectUri: null,
            apiGateway: this.apiURL,
            onSuccess: (data) => {
              console.log('------ codeToToken ------');
              console.log(data);
              setTimeout(() => {
                resolve(true);
              }, 100);
            },
            onError: (err: any) => {
              this.logger.error(`${PrimusBackendInstanceService.logID} -- Exchanging code for token failed: ${err}`);
              resolve(false);
            }
          });
        } catch (e) {
          this.logger.error(`${PrimusBackendInstanceService.logID} -- ${e}`);
          reject(new Error('Exchanging code for token failed'));
        }
      } else {
        resolve(false);
      }
    });
  }

  private broadcastAuthStatus() {
    this.authenticated.next({user: this.userAuthenticated, api: this.apiAuthenticated});
  }

  private eCultureRefreshToken(): Promise<boolean> {
    console.log('-------- eCultureRefreshToken()');
    return new Promise<boolean>((resolve, reject) => {
      refreshToken({
        apiGateway: this.apiURL,
        onSuccess: () => {
          setTimeout(() => {
            resolve(true);
          }, 200);
        },
        onError: (error: Error) => {
          this.logger.error('Error refreshing token', error);
          reject(error)
        }
      });

    });
  }

  private eCultureLogin() {
    login({
      locationPostLogin: null,
      redirectUri: null,
      loginHost: this.loginURL
    });
  }

  private generateCorrelationId() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
      s4() + '-' + s4() + s4() + s4();
  }
}

export default PrimusBackendInstanceService
